import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, Paper, Stack, TextField, Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/ErrorOutline'
import { useDispatch, useSelector } from 'react-redux'
import {
  updateAdminConfig, validateAdminSql, clearValidation,
  selectAdminValidation, selectAdminSaving,
} from '../../reducers/Beacon4PerformanceAdminReducer'

const SOURCE_FIELDS = [
  { key: 'viewName',   label: 'Source view',         width: 220 },
  { key: 'dateCol',    label: 'Date column',         width: 140 },
  { key: 'polygonCol', label: 'Polygon column',      width: 160 },
  { key: 'jvidCol',    label: 'Site (JV ID) column', width: 170 },
  { key: 'mycomCol',   label: 'Mycom column',        width: 150 },
  { key: 'vendorCol',  label: 'Vendor column',       width: 150 },
]

/**
 * Formula tab — the data-engineering surface for the selected KPI: which view
 * and columns feed it, and the SQL expression. The SQL must validate before it
 * can be saved, so a typo can no longer silently break the dashboard.
 */
export default function Beacon4PerformanceAdminFormulaPanel({ config, onSaved, notify }) {
  const dispatch   = useDispatch()
  const validation = useSelector(selectAdminValidation)
  const saving     = useSelector(selectAdminSaving)

  const [form, setForm]   = useState(config)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setForm(config)
    setDirty(false)
    dispatch(clearValidation())
  }, [config, dispatch])

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }))
    setDirty(true)
    dispatch(clearValidation())
  }

  const onValidate = () => {
    dispatch(validateAdminSql({ viewName: form.viewName, sqlExpression: form.sqlExpression }))
  }

  const validated = !!validation?.valid
  const canSave   = dirty && validated && !saving

  const onSave = async () => {
    const { id, ...dto } = { ...form, note: form.note || 'Formula updated via admin' }
    const res = await dispatch(updateAdminConfig({ id: config.id, dto }))
    if (res?.error) notify?.('Could not save formula.', 'error')
    else {
      notify?.('Formula saved.', 'success')
      setDirty(false)
      onSaved?.(dto)
    }
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5 }}>Data source</Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5}>
          {SOURCE_FIELDS.map(f => (
            <TextField
              key={f.key} label={f.label} size="small"
              value={form[f.key] ?? ''} onChange={set(f.key)} sx={{ width: f.width }}
            />
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, flex: 1 }}>SQL expression</Typography>
          <Button size="small" variant="outlined" onClick={onValidate} disabled={!form.viewName?.trim()}>
            Validate
          </Button>
        </Stack>
        <TextField
          fullWidth multiline minRows={6} maxRows={16}
          value={form.sqlExpression ?? ''} onChange={set('sqlExpression')}
          placeholder="e.g. 100.0 * SUM(ISNULL(NUM,0)) / NULLIF(SUM(ISNULL(DEN,0)),0)"
          sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 11 } }}
        />

        {validation && (
          <Alert
            severity={validation.valid ? 'success' : 'error'}
            icon={validation.valid ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
            sx={{ mt: 1, fontSize: 11, '& .MuiAlert-message': { width: '100%' } }}
          >
            {validation.valid ? (
              <>
                <strong>SQL is valid.</strong>
                {validation.detectedColumns?.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    Detected columns:{' '}
                    {validation.detectedColumns.map(c =>
                      <Chip key={c} label={c} size="small" sx={{ height: 18, fontSize: 10, mr: 0.5, mt: 0.25 }} />)}
                  </Box>
                )}
              </>
            ) : (
              <>
                <strong>Validation failed:</strong>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {(validation.errors ?? []).map((err, i) => <li key={i}>{err}</li>)}
                </ul>
                {validation.missingColumns?.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    Missing in view:{' '}
                    {validation.missingColumns.map(c =>
                      <Chip key={c} label={c} size="small" color="error" sx={{ height: 18, fontSize: 10, mr: 0.5, mt: 0.25 }} />)}
                  </Box>
                )}
              </>
            )}
          </Alert>
        )}

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
          <TextField
            label="Change note (audit log)" size="small" sx={{ flex: 1 }}
            value={form.note ?? ''} onChange={set('note')}
            placeholder="Optional — describe what you changed"
          />
          <Button variant="contained" onClick={onSave} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save formula'}
          </Button>
        </Stack>
        {dirty && !validated && (
          <Typography sx={{ fontSize: 10, color: 'warning.main', mt: 0.5 }}>
            Validate the SQL before saving.
          </Typography>
        )}
      </Paper>
    </Box>
  )
}